import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const fmtCFA = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' CFA';

const GiftCards = () => {
    const { authFetch, user } = useContext(AppContext);
    const { t } = useTranslation();

    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showIssue, setShowIssue] = useState(false);
    const [showLookup, setShowLookup] = useState(false);
    const [lookupCode, setLookupCode] = useState('');
    const [lookupResult, setLookupResult] = useState(null);
    const [toast, setToast] = useState(null);

    const [issueForm, setIssueForm] = useState({ amount: '', expiry_date: '', customer_id: '' });
    const [issuing, setIssuing] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const fetchCards = () => {
        setLoading(true);
        authFetch('/api/gift_cards').then(r => r.json()).then(d => { setCards(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
    };

    useEffect(() => { fetchCards(); }, []);

    const handleIssue = async (e) => {
        e.preventDefault();
        setIssuing(true);
        try {
            const res = await authFetch('/api/gift_cards', {
                method: 'POST',
                body: JSON.stringify({ ...issueForm, action: 'issue' })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`✅ ${t('Card Issued:')} ${data.card_code}`);
                setShowIssue(false);
                setIssueForm({ amount: '', expiry_date: '', customer_id: '' });
                fetchCards();
            } else {
                showToast(data.message || t('Failed'), 'error');
            }
        } catch {
            showToast(t('Network error'), 'error');
        }
        setIssuing(false);
    };

    const handleLookup = async () => {
        if (!lookupCode.trim()) return;
        const res = await authFetch('/api/gift_cards', {
            method: 'POST',
            body: JSON.stringify({ action: 'validate', card_code: lookupCode.trim() })
        });
        const data = await res.json();
        setLookupResult(data);
    };

    const handleDeactivate = async (id) => {
        if (!window.confirm(t('Deactivate this gift card?'))) return;
        await authFetch(`/api/gift_cards/${id}`, { method: 'DELETE' });
        showToast(t('Card deactivated'));
        fetchCards();
    };

    const columns = [
        { header: t('Card Code'), accessor: 'card_code', render: r => <span className="font-mono font-bold text-purple-700 text-lg tracking-widest">{r.card_code}</span> },
        { header: t('Customer'), accessor: 'customer_name', render: r => r.customer_name || <span className="text-gray-400 italic">{t('No customer')}</span> },
        { header: t('Initial Balance'), accessor: 'initial_balance', render: r => <span className="font-semibold">{fmtCFA(r.initial_balance)}</span> },
        { header: t('Remaining'), accessor: 'current_balance', render: r => (
            <span className={`font-bold text-base ${parseFloat(r.current_balance) > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {fmtCFA(r.current_balance)}
            </span>
        )},
        { header: t('Expiry'), accessor: 'expiry_date', render: r => r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : <span className="text-gray-400">—</span> },
        { header: t('Status'), accessor: 'is_active', render: r => r.is_active ? 
            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold">{t('Active')}</span> :
            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold">{t('Inactive')}</span>
        },
        { header: t('Actions'), accessor: 'id', render: r => (
            r.is_active ? <button onClick={() => handleDeactivate(r.id)} className="text-red-500 hover:text-red-700 text-sm font-bold">{t('Deactivate')}</button> : null
        )}
    ];

    return (
        <div className="space-y-6">
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl font-bold text-white ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`}>
                    {toast.msg}
                </div>
            )}

            <header className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        🎁 {t('Gift Cards')}
                    </h2>
                    <p className="text-gray-500 mt-1">{t('Issue, track, and manage gift cards and store credits.')}</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setShowLookup(true)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition">
                        🔍 {t('Check Card Balance')}
                    </button>
                    <button onClick={() => setShowIssue(true)} className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 shadow transition">
                        ➕ {t('Issue Gift Card')}
                    </button>
                </div>
            </header>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: t('Total Cards Issued'), value: cards.length, icon: '🎁', color: 'from-purple-500 to-purple-700' },
                    { label: t('Active Cards'), value: cards.filter(c => c.is_active).length, icon: '✅', color: 'from-emerald-500 to-emerald-700' },
                    { label: t('Total Outstanding Balance'), value: fmtCFA(cards.filter(c => c.is_active).reduce((s, c) => s + parseFloat(c.current_balance), 0)), icon: '💳', color: 'from-orange-500 to-orange-600' },
                ].map(s => (
                    <div key={s.label} className={`bg-gradient-to-br ${s.color} text-white rounded-xl p-5 shadow-md`}>
                        <p className="text-2xl mb-1">{s.icon}</p>
                        <p className="text-sm font-medium opacity-80">{s.label}</p>
                        <p className="text-2xl font-bold mt-1">{s.value}</p>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <DataTable columns={columns} data={cards} />
            </div>

            {/* Issue Modal */}
            <Modal isOpen={showIssue} onClose={() => setShowIssue(false)} title={t('Issue New Gift Card')} size="max-w-md">
                <form onSubmit={handleIssue} className="space-y-4 p-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">{t('Card Value (CFA)')}</label>
                        <input type="number" min="1" required value={issueForm.amount}
                            onChange={e => setIssueForm(f => ({ ...f, amount: e.target.value }))}
                            className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-purple-400 text-lg font-bold"
                            placeholder="e.g. 10000" />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">{t('Expiry Date (Optional)')}</label>
                        <input type="date" value={issueForm.expiry_date}
                            onChange={e => setIssueForm(f => ({ ...f, expiry_date: e.target.value }))}
                            className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-purple-400" />
                    </div>
                    <div className="flex justify-end gap-3 border-t pt-4">
                        <button type="button" onClick={() => setShowIssue(false)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-bold">{t('Cancel')}</button>
                        <button type="submit" disabled={issuing} className="px-5 py-2.5 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 shadow disabled:opacity-60">
                            {issuing ? '...' : t('Issue Card')}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Lookup Modal */}
            <Modal isOpen={showLookup} onClose={() => { setShowLookup(false); setLookupResult(null); setLookupCode(''); }} title={t('Check Gift Card Balance')} size="max-w-md">
                <div className="space-y-4 p-4">
                    <div className="flex gap-2">
                        <input type="text" value={lookupCode} onChange={e => setLookupCode(e.target.value.toUpperCase())}
                            placeholder={t('Enter card code...')}
                            className="flex-1 border p-3 rounded-lg focus:ring-2 focus:ring-purple-400 font-mono text-lg uppercase tracking-widest" />
                        <button onClick={handleLookup} className="px-4 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700">
                            🔍
                        </button>
                    </div>
                    {lookupResult && (
                        <div className={`p-4 rounded-xl border-2 ${lookupResult.valid ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'}`}>
                            {lookupResult.valid ? (
                                <div>
                                    <p className="font-bold text-emerald-700 text-lg">{t('Valid Card ✅')}</p>
                                    <p className="text-3xl font-black text-emerald-600 mt-2">{fmtCFA(lookupResult.balance)}</p>
                                    <p className="text-sm text-gray-500 mt-1">{t('Available Balance')}</p>
                                </div>
                            ) : (
                                <p className="font-bold text-red-700">{lookupResult.message || t('Card not found or inactive')}</p>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default GiftCards;
